import { useRef, useState } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { t } from "../../locale";

/**
 * The managed Turnstile widget, shared by every screen that talks to Supabase
 * Auth. plan.md §5: Turnstile is configured on Supabase and verified by
 * Supabase — our backend never sees the token and holds no Turnstile secret.
 *
 * Returns the widget to render and the token to submit. `submit` stays disabled
 * until a token exists, so a form can never be sent into a guaranteed refusal.
 */

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

export interface Captcha {
  token: string | null;
  /** True when no widget is configured, so a form must not wait for a token. */
  bypassed: boolean;
  widget: React.ReactNode;
  reset: () => void;
}

export const useCaptcha = (onError: (message: string) => void): Captcha => {
  const ref = useRef<TurnstileInstance | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const reset = () => {
    setToken(null);
    ref.current?.reset();
  };

  return {
    token,
    bypassed: !SITE_KEY,
    reset,
    widget: SITE_KEY ? (
      <div className="flex justify-center">
        <Turnstile
          ref={ref}
          siteKey={SITE_KEY}
          options={{ size: "normal" }}
          onSuccess={setToken}
          onError={() => {
            setToken(null);
            onError(t.landing.verifyFailed);
          }}
          onExpire={reset}
        />
      </div>
    ) : null,
  };
};
